import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import moment from 'moment'

import { API, API_ROUTER } from '../../../api'
import { isAuthenticated } from '../../../helpers'

const useGetMatches = (initialFilters = {}) => {
  const [matches, setMatches] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [error, setError] = useState(null)

  const [filters, setFilters] = useState({
    myMatches: 0,
    currentGame: '',
    currentState: '',
    fromDateValue: null,
    toDateValue: null,
    ...initialFilters,
  })

  const [page, setPage] = useState(1)
  const lastRequestRef = useRef(null)

  const getMatches = useCallback(
    async (currentFilters = filters, currentPage = page) => {
      // Normalize filters to ensure consistent comparison
      const normalizedFilters = {
        ...currentFilters,
        fromDateValue: currentFilters.fromDateValue || null,
        toDateValue: currentFilters.toDateValue || null,
      }

      // Create a unique key for this request to prevent duplicate calls
      const requestKey = JSON.stringify({
        filters: normalizedFilters,
        page: currentPage,
      })

      // If this is the same request as the last one, skip it
      if (lastRequestRef.current === requestKey) {
        return
      }

      lastRequestRef.current = requestKey

      setIsLoading(true)
      setError(null)

      const route = isAuthenticated()
        ? API_ROUTER.watch.getAllMatches
        : API_ROUTER.watch.getAllMatchesPublic

      setMatches([])

      let urlParams = {}

      if (currentFilters.currentGame) {
        urlParams.game = currentFilters.currentGame
      }

      if (currentFilters.currentState) {
        urlParams.state = currentFilters.currentState
      }

      if (currentFilters.myMatches) {
        urlParams.myMatches = currentFilters.myMatches
      }

      if (normalizedFilters.fromDateValue) {
        urlParams.fromDate = moment(normalizedFilters.fromDateValue).format(
          'YYYY-MM-DD'
        )
      }

      if (normalizedFilters.toDateValue) {
        urlParams.toDate = moment(normalizedFilters.toDateValue).format(
          'YYYY-MM-DD'
        )
      }

      try {
        const response = await API.request({
          ...route,
          urlParams,
          pathKeys: {
            page: currentPage || 1,
            limit: 10,
          },
        })

        setMatches(response.matches)
        setPagination(response.paginator)
      } catch (err) {
        console.error('Error fetching matches:', err)
        setError(err)
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const handlePageChange = useCallback(value => {
    setPage(value)
  }, [])

  const handleFiltersChange = useCallback(newFilters => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }, [])

  const refreshMatches = useCallback(() => {
    getMatches(filters, page)
  }, [getMatches, filters, page])

  const matchesData = useMemo(() => matches || [], [matches])

  useEffect(() => {
    getMatches(filters, page)
  }, [getMatches, filters, page])

  return {
    matches,
    matchesData,
    isLoading,
    pagination,
    error,
    filters,
    page,
    setFilters: handleFiltersChange,
    setPage: handlePageChange,
    refreshMatches,
  }
}

export default useGetMatches
